<template>
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" static class="fixed inset-0 z-50 overflow-y-auto" @close="$emit('cancel')" :open="open">
            <div class="flex justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" :class="isMobile ? 'items-start' : 'items-center'" :style="isMobile ? 'margin-top: 8vh;' : ''">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                </TransitionChild>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="inline-block px-4 pt-5 pb-4 text-left align-middle transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6" style="overflow: visible; max-height: 90vh; overflow-y: visible;">
                        <div class="sm:flex sm:items-start">
                            <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-blue-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                                <span class="flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full ring-8 ring-white">
                                    <SwitchHorizontalIcon class="w-5 h-5 text-white" aria-hidden="true" />
                                </span>
                            </div>
                            <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                                    {{ $t("device.header.changeTenant") }}
                                </DialogTitle>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500">
                                        {{ $t("device.header.changeTenantDescription") }}
                                    </p>

                                    <Combobox v-model="selectedTenant" by="id">
                                        <ComboboxLabel class="block mt-8 text-sm font-medium text-gray-700">
                                            {{ $t("device.header.changeTenantLabel") }}
                                        </ComboboxLabel>
                                        <div class="relative mt-1">
                                            <ComboboxInput class="w-full py-2 pl-3 pr-10 bg-white border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm" :display-value="(tenant) => tenant?.name" @change="query = $event.target.value" />
                                            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                                                <SelectorIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                                            </ComboboxButton>

                                            <ComboboxOptions class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                <ComboboxOption v-if="filteredTenants.length === 0 && query !== ''" class="relative px-4 py-2 text-gray-700 cursor-default select-none">
                                                    {{ $t("common.noResults") }}
                                                </ComboboxOption>

                                                <ComboboxOption v-for="tenant in filteredTenants" :key="tenant.id" :value="tenant" as="template" v-slot="{ selected, active }">
                                                    <li :class="[
                                                        'relative cursor-default select-none py-2 pl-3 pr-9',
                                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                                                    ]">
                                                        <span :class="['block truncate', selected && 'font-semibold']">
                                                            {{ tenant.name }}
                                                        </span>

                                                        <span v-if="selected" :class="[
                                                            'absolute inset-y-0 right-0 flex items-center pr-4',
                                                            active ? 'text-white' : 'text-indigo-600',
                                                        ]">
                                                            <CheckIcon class="w-5 h-5" aria-hidden="true" />
                                                        </span>
                                                    </li>
                                                </ComboboxOption>
                                            </ComboboxOptions>
                                        </div>
                                    </Combobox>

                                    <div v-if="errorMsg" class="mt-2 text-sm text-red-600">{{ $t(errorMsg) }}</div>
                                </div>
                            </div>
                        </div>

                        <div class="mt-8 sm:mt-8 sm:flex sm:flex-row-reverse">
                            <button type="button" :class="[
                                'inline-flex justify-center w-full px-4 py-2 text-base font-medium border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm',
                                (!selectedTenant || selectedTenant.id === currentTenant?.id || !hasAdminForSelected)
                                    ? 'text-gray-400 bg-gray-300 cursor-not-allowed'
                                    : 'text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                            ]" :disabled="!selectedTenant || selectedTenant.id === currentTenant?.id || !hasAdminForSelected" @click="handleConfirm">
                                {{ $t("common.action.save") }}
                            </button>
                            <button type="button" class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm" @click="$emit('cancel')" ref="cancelButtonRef">
                                {{ $t("common.action.cancel") }}
                            </button>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import {
    Combobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
    ComboboxLabel,
} from "@headlessui/vue";
import { CheckIcon, SelectorIcon, SwitchHorizontalIcon } from "@heroicons/vue/outline";
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";

export default {
    name: "ChangeTenant",
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        Combobox,
        ComboboxInput,
        ComboboxButton,
        ComboboxOptions,
        ComboboxOption,
        ComboboxLabel,
        CheckIcon,
        SelectorIcon,
        SwitchHorizontalIcon,
    },
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        tenants: {
            type: Array,
            required: true,
        },
        currentTenant: {
            type: Object,
            default: null,
        },
    },
    emits: ["confirm", "cancel"],
    setup(props, { emit }) {
        const store = useStore();
        const selectedTenant = ref(null);
        const query = ref("");
        const errorMsg = ref("");
        const isMobile = ref(false);

        onMounted(() => {
            isMobile.value = window.innerWidth < 640;
            watch(() => props.open, (open) => {
                if (open) document.body.classList.add("overflow-hidden");
                else document.body.classList.remove("overflow-hidden");
            });

        });

        onUnmounted(() => {
            document.body.classList.remove("overflow-hidden");
        });

        watch(() => props.open, (isOpen) => {
            if (isOpen) {
                selectedTenant.value = props.currentTenant;
                query.value = "";
                errorMsg.value = "";
            }
        });

        const filteredTenants = computed(() => {
            return query.value === ""
                ? props.tenants
                : props.tenants.filter((tenant) =>
                    tenant.name.toLowerCase().includes(query.value.toLowerCase())
                );
        });

        const userRoles = computed(() => store.getters["auth/userRoles"]);
        const isSuperAdmin = computed(() => store.getters["auth/isSuperAdmin"]);
        const hasAdminForSelected = computed(() => {
            if (!selectedTenant.value) return false;
            if (isSuperAdmin.value) return true;
            const requiredPermission = `${selectedTenant.value.slug || selectedTenant.value.id}:ADMIN`;
            return userRoles.value.includes(requiredPermission);
        });

        watch(selectedTenant, (tenant) => {
            if (!tenant || tenant.id === props.currentTenant?.id) {
                errorMsg.value = "";
            } else if (!hasAdminForSelected.value) {
                errorMsg.value = "device.form.permissionTenant";
            } else {
                errorMsg.value = "";
            }
        });

        const handleConfirm = () => {
            if (
                selectedTenant.value &&
                selectedTenant.value.id !== props.currentTenant?.id &&
                hasAdminForSelected.value
            ) {
                emit("confirm", selectedTenant.value);
            }
        };

        return {
            selectedTenant,
            query,
            filteredTenants,
            handleConfirm,
            hasAdminForSelected,
            errorMsg,
            isMobile,
        };
    },
};
</script>
